/* SpawnAnything — grid flow fields for mass pathing (pure, worker-safe).
   Passability is precomputed once per terrain as an 8-bit move mask per cell; a flow field is a
   multi-source Dijkstra (Dial's bucket queue, costs 10 orthogonal / 14 diagonal) from e.g. all
   enemy cells, plus a per-cell direction toward the nearest source. */
import { canStep, isWalkableCell } from '../world/terrain.js';

// direction d: 0:+x 1:+x+z 2:+z 3:-x+z 4:-x 5:-x-z 6:-z 7:+x-z
export const DX8 = Int8Array.from([1, 1, 0, -1, -1, -1, 0, 1]);
export const DZ8 = Int8Array.from([0, 1, 1, 1, 0, -1, -1, -1]);
export const COST8 = Uint8Array.from([10, 14, 10, 14, 10, 14, 10, 14]);
const S = Math.SQRT1_2;
const VX8 = Float32Array.from([1, S, 0, -S, -1, -S, 0, S]);
const VZ8 = Float32Array.from([0, S, 1, S, 0, -S, -1, -S]);
// (dx+1) + (dz+1)*3 → direction index
const DIR_OF = Int8Array.from([5, 6, 7, 4, -1, 0, 3, 2, 1]);
export const UNREACHABLE = 65535;

/**
 * Move mask per cell: bit d set = a unit may step from the cell in direction d.
 * Diagonals only when both orthogonal detours are allowed (no corner cutting past cliffs/water).
 */
export function buildPassability(t) {
  const n = t.n, N = n * n, mask = new Uint8Array(N), walk = new Uint8Array(N);
  for (let i = 0; i < N; i++) walk[i] = isWalkableCell(t, i) ? 1 : 0;
  for (let gz = 0; gz < n; gz++) for (let gx = 0; gx < n; gx++) {
    const i = gz * n + gx; if (!walk[i]) continue;
    let m = 0;
    if (gx < n - 1 && canStep(t, i, i + 1)) m |= 1;
    if (gz < n - 1 && canStep(t, i, i + n)) m |= 4;
    if (gx > 0 && canStep(t, i, i - 1)) m |= 16;
    if (gz > 0 && canStep(t, i, i - n)) m |= 64;
    mask[i] = m;
  }
  for (let gz = 0; gz < n; gz++) for (let gx = 0; gx < n; gx++) {
    const i = gz * n + gx, m = mask[i]; if (!m) continue;
    let add = 0;
    for (let d = 1; d < 8; d += 2) {
      const dx = DX8[d], dz = DZ8[d];
      const bx = dx > 0 ? 0 : 4, bz = dz > 0 ? 2 : 6; // orthogonal directions toward x and z
      if (!(m & (1 << bx)) || !(m & (1 << bz))) continue;
      const ox = i + dx, oz = i + dz * n;
      if ((mask[ox] & (1 << bz)) && (mask[oz] & (1 << bx))) add |= 1 << d;
    }
    mask[i] = m | add;
  }
  const offs = new Int32Array(8);
  for (let d = 0; d < 8; d++) offs[d] = DX8[d] + DZ8[d] * n;
  return { n, N, cell: t.cell, origin: t.origin, mask, walk, offs };
}

/**
 * Multi-source shortest paths toward `sources` (cell indices). Deterministic (LIFO buckets in fixed
 * order, ties in direction resolved by lowest index). Pass a previous field as `out` to reuse buffers.
 * Returns { n, dist: Uint16Array (65535 = unreachable), dir: Int8Array (0..7, -1 = none), mask }.
 */
export function computeFlowField(pass, sources, out) {
  const N = pass.N, mask = pass.mask, offs = pass.offs, walk = pass.walk;
  const dist = out && out.dist && out.dist.length === N ? out.dist : new Uint16Array(N);
  const dir = out && out.dir && out.dir.length === N ? out.dir : new Int8Array(N);
  // bucket queue: ring of 16 buckets (max edge cost 14), nodes in a shared linked pool.
  // Each cell improves at most once per popped neighbour, so 8N + sources entries always suffice.
  const cap = N * 9 + 64;
  let q = pass._q;
  if (!q || q.cellOf.length < cap) q = pass._q = { head: new Int32Array(16), cellOf: new Int32Array(cap), next: new Int32Array(cap) };
  const head = q.head, cellOf = q.cellOf, next = q.next;
  head.fill(-1);
  let top = 0, pending = 0;
  dist.fill(UNREACHABLE);
  for (let k = 0; k < sources.length; k++) {
    const s = sources[k];
    if (s < 0 || s >= N || !walk[s] || dist[s] === 0) continue;
    dist[s] = 0; cellOf[top] = s; next[top] = head[0]; head[0] = top++; pending++;
  }
  for (let cur = 0; pending > 0 && cur < UNREACHABLE; cur++) {
    const b = cur & 15;
    while (head[b] !== -1) {
      const e = head[b]; head[b] = next[e]; pending--;
      const c = cellOf[e];
      if (dist[c] !== cur) continue; // stale entry
      const m = mask[c];
      for (let d = 0; d < 8; d++) {
        if (!(m & (1 << d))) continue;
        const nb = c + offs[d], nd = cur + COST8[d];
        if (nd < dist[nb]) {
          dist[nb] = nd;
          const bb = nd & 15; cellOf[top] = nb; next[top] = head[bb]; head[bb] = top++; pending++;
        }
      }
    }
  }
  for (let c = 0; c < N; c++) {
    const dc = dist[c];
    if (dc === 0 || dc === UNREACHABLE) { dir[c] = -1; continue; }
    const m = mask[c]; let bd = -1, bv = dc;
    for (let d = 0; d < 8; d++) {
      if (!(m & (1 << d))) continue;
      const v = dist[c + offs[d]] + COST8[d];
      if (v < bv || (v === bv && bd < 0)) { bv = v; bd = d; }
    }
    dir[c] = bd;
  }
  return { n: pass.n, cell: pass.cell, origin: pass.origin, dist, dir, mask };
}

/**
 * Smooth flow direction at a world position: bilinear blend of the 4 surrounding cell directions,
 * using only cells reachable in one step from the unit's own cell. Writes a unit vector to
 * out[0] (x), out[1] (z); returns false (and 0,0) when there is no direction (source cell / unreachable).
 */
export function sampleFlow(field, t, x, z, out) {
  const n = t.n, cell = t.cell, gxf = (x - t.origin) / cell, gzf = (z - t.origin) / cell;
  let ox = Math.floor(gxf), oz = Math.floor(gzf);
  if (ox < 0) ox = 0; else if (ox >= n) ox = n - 1;
  if (oz < 0) oz = 0; else if (oz >= n) oz = n - 1;
  const c0 = oz * n + ox, dir = field.dir, d0 = dir[c0];
  if (d0 < 0) { out[0] = 0; out[1] = 0; return false; }
  const fx = gxf - 0.5, fz = gzf - 0.5, ix = Math.floor(fx), iz = Math.floor(fz), tx = fx - ix, tz = fz - iz;
  const m0 = field.mask[c0];
  let vx = 0, vz = 0;
  for (let k = 0; k < 4; k++) {
    const gx = ix + (k & 1), gz = iz + (k >> 1);
    if (gx < 0 || gz < 0 || gx >= n || gz >= n) continue;
    const c = gz * n + gx, d = dir[c];
    if (d < 0) continue;
    if (c !== c0) { const s = DIR_OF[(gx - ox + 1) + (gz - oz + 1) * 3]; if (s < 0 || !(m0 & (1 << s))) continue; }
    const w = ((k & 1) ? tx : 1 - tx) * ((k >> 1) ? tz : 1 - tz);
    vx += VX8[d] * w; vz += VZ8[d] * w;
  }
  let len = Math.sqrt(vx * vx + vz * vz); // not Math.hypot: must be bit-identical in every engine (lockstep)
  if (len < 1e-4) { vx = VX8[d0]; vz = VZ8[d0]; len = 1; }
  out[0] = vx / len; out[1] = vz / len;
  return true;
}

/** Path distance (metres) from (x,z) to the nearest source, Infinity if unreachable. */
export function sampleDist(field, t, x, z) {
  const n = t.n;
  let gx = Math.floor((x - t.origin) / t.cell), gz = Math.floor((z - t.origin) / t.cell);
  if (gx < 0) gx = 0; else if (gx >= n) gx = n - 1;
  if (gz < 0) gz = 0; else if (gz >= n) gz = n - 1;
  const d = field.dist[gz * n + gx];
  return d === UNREACHABLE ? Infinity : d * t.cell / 10;
}

/**
 * Collect unique source cells from struct-of-arrays unit positions (alive units in [start,end)).
 * `scratch` = { stamp: Uint32Array(N), gen: number, out: Int32Array } is reused between calls.
 */
export function sourcesFromUnits(t, X, Z, ALIVE, start, end, scratch, TEAM, team) { // TEAM/team: only units of that side (optional)
  const N = t.n * t.n, n = t.n;
  if (!scratch.stamp || scratch.stamp.length !== N) { scratch.stamp = new Uint32Array(N); scratch.gen = 0; }
  if (!scratch.out || scratch.out.length < end - start) scratch.out = new Int32Array(Math.max(64, end - start));
  const st = scratch.stamp, g = scratch.gen = (scratch.gen + 1) >>> 0 || 1, out = scratch.out;
  let k = 0;
  for (let i = start; i < end; i++) {
    if ((ALIVE && !ALIVE[i]) || (TEAM && TEAM[i] !== team)) continue;
    let gx = Math.floor((X[i] - t.origin) / t.cell), gz = Math.floor((Z[i] - t.origin) / t.cell);
    if (gx < 0) gx = 0; else if (gx >= n) gx = n - 1;
    if (gz < 0) gz = 0; else if (gz >= n) gz = n - 1;
    const c = gz * n + gx;
    if (st[c] === g) continue;
    st[c] = g; out[k++] = c;
  }
  return out.subarray(0, k);
}
